import { createSelector } from 'reselect';

import { handleGetDateRangeInfo } from '../../main/support/dateUtils';

import { INIT_STATE } from '../reducers/ShareableReducer';
import type {
  ShareableReducer,
  ShareableData,
  SingleNamespaceData,
  BookmarkReports,
  OrderBy,
} from '../types/shareableTypes';

import { filterSelectedColumns } from '../support/utils/filterSelectedColumns';
import { getColumnDataFieldKey } from '../support/utils';
import type { RootState } from 'client/js/redux';

export type Props = {
  shareableNamespace?: string;
  context?: string;
};

export function getState(state: RootState): ShareableReducer {
  return (state && state.shareable) || INIT_STATE;
}

export const getBookmarkReports = createSelector(getState, (data) => {
  return data.bookmarked_reports || [];
});

export const isLoading: (state: RootState) => boolean = createSelector(getState, (state) => Boolean(state.loading));

export const areBookmarkReportsLoaded: (state: RootState) => boolean = createSelector(getState, (state) =>
  Boolean(state.areBookmarkReportsLoaded)
);

export const getBookmarkReportsByNamespace: (state: RootState, props: Props) => BookmarkReports = createSelector(
  getBookmarkReports,
  (state, props) => props.shareableNamespace,
  (bookmarkReports, shareableNamespace) => {
    return bookmarkReports.filter((report) => report.namespace === shareableNamespace);
  }
);

const selectShareableData: (state: RootState) => ShareableData = createSelector(getState, (shareableState) => {
  return shareableState.data;
});

export const selectSingleNamespaceData: (state: RootState, props: Props) => SingleNamespaceData = createSelector(
  selectShareableData,
  (state, props) => props.shareableNamespace,
  (shareableData, shareableNamespace) => {
    return shareableData[shareableNamespace] || {};
  }
);

export const selectPageStateForContext: (state: RootState, props: Props) => any = createSelector(
  selectSingleNamespaceData,
  (state, props) => props.context,
  (singleNamespaceData, context) => {
    return context ? singleNamespaceData[context || ''] || {} : {};
  }
);

export const selectNamespaceDateRangeInfo: (state: RootState, props: Props) => SingleNamespaceData = createSelector(
  selectSingleNamespaceData,
  (shareableData) => {
    const { dateRange, defaultRange } = shareableData;
    const dateRangeInfo = {
      dateRange,
      defaultRange,
    };

    return handleGetDateRangeInfo(dateRangeInfo);
  }
);

export const selectFilteredNamespaceData: (state: RootState, props: Props) => SingleNamespaceData = createSelector(
  selectSingleNamespaceData,
  (state) => state.accessControl.data,
  filterSelectedColumns
);

export const selectNamespaceOrderBy: (state: RootState, props: Props) => OrderBy | undefined = createSelector(
  selectFilteredNamespaceData,
  (namespaceData) => {
    if (!namespaceData.activeHeader) {
      return;
    }
    const selectedColumns = namespaceData.selectedColumns || [];
    const header = selectedColumns.find((column) => column.key === namespaceData.activeHeader);
    if (!header) {
      return;
    }
    const field = getColumnDataFieldKey(header.type, header.key);
    const direction = namespaceData.isReversedHeader ? 'ascending' : 'descending';
    return { field, direction };
  }
);
